import type { WorkbookColumnarSheet } from "@erp/shared-types";
import {
  EMPTY_LINEAGE,
  formulaColumnNames,
  formulaLookupLineage,
  formulaOrigin,
  type FieldRef,
  type SheetLineage,
  type SheetLineageLookup,
} from "../lineage";
import type { SheetPlan } from "../planTypes";

type Stages = NonNullable<SheetPlan["stages"]>;

/** What a stage adds to a Worksheet's lineage. */
interface StageLineage {
  lookups: SheetLineageLookup[];
  reads: FieldRef[];
}

/**
 * The Lookups stage: each Lookup's looked-up and matched columns, and the
 * joined Fields (input columns, then each Lookup's output) by lowercase name.
 */
function lookupStage(
  sheet: WorkbookColumnarSheet,
  stages: Stages,
): StageLineage & { joined: Map<string, FieldRef> } {
  const joined = new Map(
    stages.input.map((field): [string, FieldRef] => [
      field.name.toLowerCase(),
      { tableId: sheet.inputId, column: field.name },
    ]),
  );
  const lookups: SheetLineageLookup[] = [];
  const reads: FieldRef[] = [];
  for (const { lookup } of stages.lookups) {
    const value = joined.get(lookup.inputColumn.toLowerCase()) ?? null;
    const match = { tableId: lookup.sourceId, column: lookup.matchColumn };
    const output = { tableId: lookup.sourceId, column: lookup.valueColumn };
    lookups.push({
      value,
      match,
      missColumn: lookup.missing === "na" ? lookup.outputColumn : null,
    });
    reads.push(match, output, ...(value ? [value] : []));
    joined.set(lookup.outputColumn.toLowerCase(), output);
  }
  return { joined, lookups, reads };
}

/** A Formula column's lookups; the outermost one of a column that is one lookup without a fallback holds #N/A exactly on its misses. */
function columnLookups(
  column: string,
  formula: Stages["columns"][number]["formula"],
  lookups: readonly SheetLineageLookup[],
): SheetLineageLookup[] {
  const whole = formula.kind === "lookup" && !formula.notFound;
  return lookups.map((lookup, index) =>
    whole && index === lookups.length - 1
      ? { ...lookup, missColumn: column }
      : lookup,
  );
}

/**
 * A columnar Worksheet's lineage: its input columns, Lookup outputs and
 * Formula columns that are a reference or a lookup's returned column, traced
 * one table back. Unknown until its stages are planned.
 */
export function columnarLineage(
  sheet: WorkbookColumnarSheet,
  plan: SheetPlan,
): SheetLineage {
  const stages = plan.stages;
  if (!stages) return EMPTY_LINEAGE;
  const { joined, lookups, reads } = lookupStage(sheet, stages);
  // Formula columns in dependency order: a reference reads an earlier Formula
  // column of that name, else the joined Field it shadows.
  const computed = new Map<string, FieldRef | null>();
  const resolve = (name: string): FieldRef | null => {
    const lower = name.toLowerCase();
    return computed.has(lower)
      ? (computed.get(lower) ?? null)
      : (joined.get(lower) ?? null);
  };
  const resolved = (names: readonly string[]) =>
    names.flatMap((name) => resolve(name) ?? []);
  for (const { column, formula } of stages.columns) {
    reads.push(...resolved(formulaColumnNames(formula)));
    const lineage = formulaLookupLineage(formula, resolve);
    lookups.push(...columnLookups(column.name, formula, lineage.lookups));
    reads.push(...lineage.reads);
    computed.set(column.name.toLowerCase(), formulaOrigin(formula, resolve));
  }
  reads.push(
    ...resolved([
      ...sheet.filters.map((filter) => filter.column),
      ...sheet.sort.map((sort) => sort.column),
    ]),
  );
  const fields = new Map<string, FieldRef>();
  for (const [name, field] of computed) if (field) fields.set(name, field);
  return { fields, lookups, reads };
}
